import { useQuery } from "@tanstack/react-query";

const baseUrl = import.meta.env.VITE_API_ENDPOINT || "";
const websiteSlug = import.meta.env.VITE_WEBSITE_SLUG || "default";

const LOCAL_DATA_SECTIONS = [
  "info",
  "gallery",
  "workdays",
  "bookings",
  "events",
] as const;

async function loadLocalSection(section: string): Promise<unknown> {
  if (!LOCAL_DATA_SECTIONS.includes(section as (typeof LOCAL_DATA_SECTIONS)[number])) {
    throw new Error(`Unknown data section: ${section}`);
  }

  const jsonModule = await import(`../data/${section}.json`);
  const jsonData = jsonModule.default || jsonModule;
  return jsonData.data ?? jsonData;
}

export const fetchJsonData = async (section: string) => {
  if (!baseUrl) {
    console.warn(`API not configured for ${section}. Using local data.`);
    return loadLocalSection(section);
  }

  try {
    const response = await fetch(`${baseUrl}/${websiteSlug}/json-public/${section}/`);

    if (response.ok) {
      const result = await response.json();
      return result.data !== undefined ? result.data : result;
    }

    console.warn(
      `API fetch for '${section}' failed (${response.status}). Falling back to local data.`
    );
    return loadLocalSection(section);
  } catch (error) {
    console.warn(`API fetch for '${section}' failed. Falling back to local data.`, error);
    return loadLocalSection(section);
  }
};

export const useJsonData = (section: string, staleTime: number = 300000) => {
  return useQuery({
    queryKey: ["json", section],
    queryFn: () => fetchJsonData(section),
    retry: 0,
    staleTime,
    select: (data) => data ?? {},
  });
};
